import React from 'react';
import { Card, OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './bloodwork-anemia-status.css';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import { ProbabilityCategory } from '../../models/ProbabilityCategory';
import {
  ironGauge,
  ironPatternArrows,
  IronCondition,
  IronGauge,
  IronGaugeInput,
  IronMarkerArrow,
} from '../../models/IronAnalysis';
import { getIronPatternInsight, IronPatternInsight } from '../../models/iron-pattern-insights';
import { LabsmartsGuage } from '../charts/LabsmartsGauge';
import { MARKER_ROW_STYLE } from './pattern-box-styles';

// Up/down arrows for the marker row — same assets the Glucose Insights panel uses.
const arrow_up = require('../../assets/imgs/long-arrow-up-8x13.png');
const arrow_down = require('../../assets/imgs/long-arrow-down-8x12.png');

const arrowStyle: React.CSSProperties = {
  height: '11px',
  margin: '0 3px',
  verticalAlign: 'middle',
  position: 'relative',
  top: '-0.5px',
};

type Props = { bloodworkAnalysis: BloodworkAnalysis };

// Gauge-label prefix per condition. The likelihood word + percent are appended
// by the gauge, so this reads e.g. "Iron Insufficiency is Likely (80%)". Each
// is the family name of the pattern box beneath it (the existing RBC root-cause
// labels), so the needle and the box never name the finding two ways.
const CONDITION_LABEL: Record<IronCondition, string> = {
  insufficiency: 'Iron Insufficiency',
  storage: 'Iron Storage Insufficiency',
  serum: 'Serum Iron Insufficiency',
  excess: 'Serum Iron Excess',
  imbalance: 'Iron Imbalance',
  balanced: 'Iron Imbalance',
};

/**
 * Iron Insights — the Iron Status & Utilization section's panel.
 *
 * Gauge + "Iron Imbalance Consistent with:" boxes, in the Glucose / Stress &
 * Adrenal mold. The pattern logic is the iron-quartet part of the RBC
 * section's existing "Root Causes w Iron" rules, lifted into
 * IronAnalysis.resolveIronPattern; see the header there for what was kept
 * and what was deliberately left to the RBC panel.
 *
 * Gated `visibleExceptProd()` and listed in SECTIONS_PENDING_CLINICAL_REVIEW
 * (screen-only, with the design-preview notice) until the pattern set and the
 * gauge ladder have been reviewed. Everything the PDF needs is wired
 * (printsInsightsPanel, the pdf-section branch, estimatedPdfHeight and the
 * packer), so leaving that list is the whole release.
 */
export class IronStatusInsights extends React.Component<Props, {}> {
  /*
   * Explains the gauge — mirrors the Thyroid / Stress & Adrenal header
   * info-circle Popover.
   *
   * THIS COPY DESCRIBES ironGauge() AND resolveIronPattern() IN IronAnalysis.ts.
   * Read them before editing it, and edit it whenever they change (the adrenal
   * popover taught a rule its gauge had dropped for months).
   */
  gaugeInfoPopover = () => (
    <Popover id="iron-status-gauge-info" style={{ maxWidth: '340px' }}>
      <Popover.Title as="div" style={{ fontSize: '13px', fontWeight: 700, backgroundColor: '#fff', borderBottom: 'none' }}>
        About this gauge
      </Popover.Title>
      <Popover.Content>
        <div style={{ textAlign: 'left', fontSize: '12px', color: '#555', lineHeight: 1.45 }}>
          <div style={{ marginBottom: '8px' }}>
            This gauge reads the iron picture on the same 0–100% scale as the other panels, from{' '}
            <strong>Iron</strong>, <strong>TIBC</strong>, <strong>% Saturation</strong> and{' '}
            <strong>Ferritin</strong>. <strong>Ferritin and % Saturation set the direction:</strong>
          </div>
          <ul style={{ paddingLeft: '16px', marginBottom: '8px' }}>
            <li>
              <strong>Ferritin below optimal</strong> — points toward iron insufficiency (% Saturation
              also low) or iron storage insufficiency (% Saturation optimal, or not run).
            </li>
            <li>
              <strong>% Saturation below optimal with ferritin held</strong> — points toward serum iron
              insufficiency, the picture inflammation, oxidative stress or infection produces by holding
              iron in storage.
            </li>
            <li>
              <strong>% Saturation above optimal</strong> — points toward serum iron excess;{' '}
              <strong>ferritin above optimal on its own</strong> reads as inflammation or oxidative
              stress rather than an iron finding.
            </li>
          </ul>
          <div style={{ marginBottom: '8px' }}>
            The needle scores how far the markers behind that finding sit from optimal, the way the
            Stress &amp; Adrenal gauge does: one marker outside optimal reads <strong>possible</strong>,
            two read higher, and a marker outside the lab range reads <strong>likely</strong> or above.
          </div>
          <div style={{ marginBottom: '8px' }}>
            It reads the quartet as <strong>two measures, not four markers</strong>. Iron, TIBC and
            % Saturation score together as one reading of iron transport, because % Saturation is
            calculated from the other two and scoring it on its own would count one result twice.
            Ferritin scores beside them as the storage reading. A marker inside the lab range that
            points away from the finding is left out of the score; a marker outside the lab range
            always counts, whichever finding it points at.
          </div>
          <div style={{ marginBottom: 0 }}>
            <strong>Absolute neutrophils</strong> below optimal refines a low-ferritin finding toward
            copper insufficiency, and the <strong>WBC</strong> refines a serum iron finding toward chronic
            or acute infection. These are the same rules the Blood Oxygen &amp; Red Blood Cell panel
            already applies; here they read the iron markers without waiting for the red cells to change.
          </div>
        </div>
      </Popover.Content>
    </Popover>
  );

  static gaugeInput(ba: BloodworkAnalysis): IronGaugeInput {
    return {
      iron: ba.iron,
      tibc: ba.tibc,
      saturation: ba.saturation,
      ferritin: ba.ferritin,
      neutrophilsAbs: ba.neutrophilsAbs,
      wbc: ba.wbc,
    };
  }

  /** RBC category 73's condition: WBC optimal, Immune System Abnormality Possible or higher. */
  static immuneAbnormalityPossible(ba: BloodworkAnalysis): boolean {
    const category = ba.wbcAnalysis?.immuneSystemDisorderProbabilityCategory;
    return (
      category === ProbabilityCategory.POSSIBLE ||
      category === ProbabilityCategory.LIKELY ||
      category === ProbabilityCategory.HIGHLY_LIKELY
    );
  }

  static gaugeFor(ba: BloodworkAnalysis): IronGauge | null {
    return ironGauge(
      IronStatusInsights.gaugeInput(ba),
      IronStatusInsights.immuneAbnormalityPossible(ba),
    );
  }

  /**
   * Whether this panel renders anything. Mirrors render()'s own guard: the gauge
   * needs at least one usable marker of the iron quartet. Callers use it to drop
   * the evaluation column entirely rather than leave an empty gap.
   */
  static shouldRender(ba: BloodworkAnalysis): boolean {
    if (!ba?.bloodwork) return false;
    return !!IronStatusInsights.gaugeFor(ba);
  }

  /**
   * Serializable view of this panel for one test — the gauge and its pattern
   * box — mirroring render() exactly. Written for the raw-data JSON export,
   * which does NOT call it yet: the panel is pending clinical review, and held
   * work is not exported (see AIExportBuilder). Null when the panel would not
   * render at all.
   */
  static exportInsights(ba: BloodworkAnalysis): {
    gauge: {
      label: string;
      condition: string;
      category: string;
      probability: number;
      markers: IronMarkerArrow[];
    };
    patterns: Array<{
      categoryId: number;
      patternNumber: string;
      title: string;
      bullets: string[];
      markers: IronMarkerArrow[];
    }>;
  } | null {
    if (!ba?.bloodwork) return null;
    const gauge = IronStatusInsights.gaugeFor(ba);
    if (!gauge) return null;
    const insight = gauge.patternId != null ? getIronPatternInsight(gauge.patternId) : undefined;
    const patterns =
      insight && gauge.patternId != null
        ? [
            {
              categoryId: gauge.patternId,
              patternNumber: insight.patternNumber,
              title: IronStatusInsights.patternTitle(insight),
              bullets: insight.bullets,
              markers: ironPatternArrows(gauge.patternId, IronStatusInsights.gaugeInput(ba)),
            },
          ]
        : [];
    return {
      gauge: {
        label: `${CONDITION_LABEL[gauge.condition]} is`,
        condition: gauge.condition,
        category: gauge.category,
        probability: gauge.probability,
        markers: gauge.markers,
      },
      patterns,
    };
  }

  /*
   * ── PDF pagination ─────────────────────────────────────────────────────────
   *
   * The PDF packer needs this panel's height BEFORE React renders it, the same
   * way Glucose and Stress & Adrenal self-estimate. The panel is the same
   * markup as the Stress & Adrenal one (gauge, then .condition-section boxes
   * with a title line, bullets and a marker row), so its measured constants
   * are carried over unchanged. Re-measure (headless Chrome over the compiled
   * custom.scss at the real 300px width, see the PDF geometry harness) before
   * this panel leaves clinical review and starts printing.
   */
  // Card header + .card-body padding + the 5px panel border, top and bottom.
  static readonly PDF_CARD_CHROME = 93;
  // .anemiaStatusContainer's min-height. The gauge alone already clears it.
  static readonly PDF_MIN_BODY_HEIGHT = 150;
  // The Highcharts container, at the height its own options declare
  // (chart.height 200 in LabsmartsGuage).
  static readonly PDF_GAUGE_HEIGHT = 200;
  // Bootstrap's body line-height (1.5) at the panel's 12px font.
  static readonly PDF_LINE_HEIGHT = 18;
  // A pattern box minus its text: border, padding, and the ul's own 1rem
  // bottom margin.
  static readonly PDF_PATTERN_BOX_HEIGHT = 32;
  // .condition-section's `margin: 10px 0`, paid once before the box and once
  // more after it.
  static readonly PDF_PATTERN_BOX_MARGIN = 10;
  // Average glyph width at 12px bold sans-serif (the whole box is bold).
  static readonly PDF_CHAR_WIDTH = 6.9;
  // Text width inside a pattern box, and inside a bullet (which also gives up
  // its glyph and the 3px margin after it).
  static readonly PDF_BOX_WIDTH = 254;
  static readonly PDF_BULLET_WIDTH = 246;
  // Per marker in the ▲/▼ row: the glyph with its margins, plus the ", ".
  static readonly PDF_MARKER_SLOT_WIDTH = 13;
  static readonly PDF_MARKER_SEPARATOR_CHARS = 2;

  /** Rendered lines a string wraps to in `width` px at the panel's body font. */
  static pdfLines(text: string, width: number): number {
    return Math.max(1, Math.ceil((text.length * IronStatusInsights.PDF_CHAR_WIDTH) / width));
  }

  /** The box's heading: the title alone unless there are bullets for it to introduce. */
  static patternTitle(insight: IronPatternInsight): string {
    return insight.bullets.length > 0 && insight.consistentWith
      ? `${insight.title} Consistent with:`
      : insight.title;
  }

  /** The ▲/▼ row under a box, which wraps between whole markers. */
  static markerRowHeight(markers: IronMarkerArrow[]): number {
    const I = IronStatusInsights;
    if (!markers.length) return 0;
    const chars =
      markers.reduce((n, m) => n + m.label.length, 0) +
      (markers.length - 1) * I.PDF_MARKER_SEPARATOR_CHARS;
    const width = markers.length * I.PDF_MARKER_SLOT_WIDTH + chars * I.PDF_CHAR_WIDTH;
    return Math.max(1, Math.ceil(width / I.PDF_BOX_WIDTH)) * I.PDF_LINE_HEIGHT;
  }

  /** One pattern box, including the margin above it. */
  static patternBoxHeight(insight: IronPatternInsight, markers: IronMarkerArrow[]): number {
    const I = IronStatusInsights;
    const bullets = insight.bullets.reduce(
      (h, bullet) => h + I.pdfLines(bullet, I.PDF_BULLET_WIDTH) * I.PDF_LINE_HEIGHT,
      0,
    );
    return (
      I.PDF_PATTERN_BOX_MARGIN +
      I.PDF_PATTERN_BOX_HEIGHT +
      I.pdfLines(I.patternTitle(insight), I.PDF_BOX_WIDTH) * I.PDF_LINE_HEIGHT +
      bullets +
      I.markerRowHeight(markers)
    );
  }

  /**
   * This panel's rendered height, in the capture pixels every other number in
   * the PDF's page arithmetic uses (see pdfPanelMetrics). Built from
   * exportInsights so the estimate and the render cannot drift apart.
   */
  static estimatedPdfHeight(ba: BloodworkAnalysis): number {
    const I = IronStatusInsights;
    const view = I.exportInsights(ba);
    if (!view) return 0;
    const boxes = view.patterns.reduce((sum, pattern) => {
      const insight = getIronPatternInsight(pattern.categoryId);
      return insight ? sum + I.patternBoxHeight(insight, pattern.markers) : sum;
    }, 0);
    return (
      I.PDF_CARD_CHROME +
      Math.max(I.PDF_GAUGE_HEIGHT + boxes, I.PDF_MIN_BODY_HEIGHT) +
      (boxes ? I.PDF_PATTERN_BOX_MARGIN : 0)
    );
  }

  renderArrow = (direction: 'up' | 'down', label: string) => {
    const src = direction === 'up' ? arrow_up : arrow_down;
    const alt = direction === 'up' ? '↑' : '↓';
    return (
      <span style={{ whiteSpace: 'nowrap' }}>
        <img src={src} alt={alt} style={arrowStyle} />
        {label}
      </span>
    );
  };

  // Client-facing pattern box under the gauge — mirrors the Glucose and Stress
  // & Adrenal panels: "{title} Consistent with:" + the finding(s) + the driving
  // markers. One box for the resolved pattern; nothing renders when no pattern
  // fires (the gauge still reads the deviating markers as "Iron Imbalance").
  renderPatternBox = (gauge: IronGauge, input: IronGaugeInput) => {
    if (gauge.patternId == null) return null;
    const insight = getIronPatternInsight(gauge.patternId);
    if (!insight) return null;
    const markers = ironPatternArrows(gauge.patternId, input);
    return (
      <div className="condition-section" key={gauge.patternId}>
        <div style={{ color: '#000', textAlign: 'left' }}>
          {IronStatusInsights.patternTitle(insight)}
        </div>
        {insight.bullets.length > 0 && (
          <ul style={{ paddingLeft: 0, marginLeft: 0, listStyle: 'none', textAlign: 'left' }}>
            {insight.bullets.map((bullet) => (
              // Hanging indent, matching the other panels' boxes: a wrapped
              // line stays clear of the glyph. The serum-iron labels run to
              // two lines in a 246px box, so it matters here.
              <li key={bullet} style={{ display: 'flex', alignItems: 'flex-start' }}>
                <span style={{ display: 'inline-block', marginRight: '3px' }}>•</span>
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        )}
        {markers.length > 0 && (
          <div style={MARKER_ROW_STYLE}>
            {markers.map((m, index) => (
              <React.Fragment key={`${m.label}-${index}`}>
                {index > 0 && <>,{' '}</>}
                {this.renderArrow(m.direction, m.label)}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  };

  render() {
    const { bloodworkAnalysis: ba } = this.props;
    if (!ba.bloodwork) return null;

    // gaugeFor() rather than an inline copy of the same call: shouldRender,
    // exportInsights and estimatedPdfHeight all go through it, and render
    // drifting from those three is what lets the screen and the PDF disagree.
    const gauge = IronStatusInsights.gaugeFor(ba);
    if (!gauge) return null;

    return (
      // Standard 300px eval panel — same size as the Glucose / Immune / RBC panels.
      <Card className="evaluationPanel" id="iron-status-insights">
        <Card.Header className="evaluationPanelHeading">
          Iron Insights
          <OverlayTrigger placement="bottom" overlay={this.gaugeInfoPopover()}>
            <FontAwesomeIcon
              icon="info-circle"
              style={{ cursor: 'pointer', color: '#fff', fontSize: 14, marginLeft: 8, verticalAlign: 'middle' }}
            />
          </OverlayTrigger>
        </Card.Header>
        <Card.Body>
          <div className="anemiaStatusContainer">
            <LabsmartsGuage
              text={`${CONDITION_LABEL[gauge.condition]} is`}
              chartContainerId="ironStatusGuage"
              bloodwork={ba.bloodwork}
              probability={gauge.probability}
              category={gauge.category}
              ticks={[0, 50, 70, 90]}
            />
            {this.renderPatternBox(gauge, IronStatusInsights.gaugeInput(ba))}
          </div>
        </Card.Body>
      </Card>
    );
  }
}
